import { motion } from "framer-motion";
import { IMAGES } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const CHAPTERS = [
  { num: "01", title: "Move Better", desc: "Everything starts with quality of movement. We rebuild the patterns your body was designed for." },
  { num: "02", title: "Get Stronger", desc: "Strength built on sound movement is strength you can actually use — anywhere." },
  { num: "03", title: "Perform Better", desc: "Carry, climb, sprint, react. Capability compounds into athletic performance." },
  { num: "04", title: "Live Better", desc: "The real scoreboard is outside the gym: energy, confidence, independence for life." },
];

export default function Philosophy() {
  return (
    <section id="method" data-testid="philosophy-section" className="relative bg-ink">
      <div className="grid lg:grid-cols-2">
        <div className="lg:sticky lg:top-0 lg:h-screen flex flex-col justify-center px-5 sm:px-10 lg:px-16 py-24 lg:py-0">
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-volt mb-6">
              The Capable 360 Philosophy
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display uppercase leading-[0.88] text-5xl sm:text-6xl xl:text-7xl">
              Fitness isn't just about lifting more.
              <span className="block text-volt mt-2">It's about being more capable.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-8 text-zinc-400 text-base sm:text-lg leading-relaxed max-w-lg">
              Capable 360 is a movement-based training system designed to improve strength,
              endurance, cardiovascular fitness, mobility, flexibility, balance and coordination —
              through movements that transfer to everyday life.
            </p>
          </Reveal>
          <Reveal delay={0.24} className="mt-10 hidden lg:block">
            <div className="relative w-full max-w-md aspect-[16/10] overflow-hidden group">
              <motion.img
                src={IMAGES.philosophy}
                alt="Functional training studio with free weights"
                className="h-full w-full object-cover grayscale-[35%] transition-transform duration-700 group-hover:scale-105"
                initial={{ clipPath: "inset(0 100% 0 0)" }}
                whileInView={{ clipPath: "inset(0 0% 0 0)" }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              />
              <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.25em] text-zinc-300 bg-ink/60 px-2 py-1">
                The studio floor
              </span>
            </div>
          </Reveal>
        </div>

        <div className="px-5 sm:px-10 lg:px-16 pb-24 lg:py-[18vh] flex flex-col gap-[16vh]">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.num}
              data-testid={`philosophy-chapter-${c.num}`}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-20%" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="border-t border-zinc-800 pt-8"
            >
              <span className="font-display text-volt text-xl tracking-widest">{c.num}</span>
              <h3 className="font-display uppercase text-5xl sm:text-6xl xl:text-7xl leading-[0.9] mt-3">
                {c.title}
                {i < CHAPTERS.length - 1 && <span className="text-zinc-700"> →</span>}
              </h3>
              <p className="mt-5 text-zinc-400 text-base sm:text-lg leading-relaxed max-w-md">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
